import { forwardRef, useImperativeHandle } from "react";
import {
  GetProgramFeeResponse,
  IdNameList,
} from "../../../../../../../../api/services/common/models";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
} from "../../../../../../../../components";
import { CurrencyFormat } from "../../../../../../../../components/textFieldCurrency/CurrencyUtils";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  MAX_LENGTH_100,
} from "../../../../../../../../helpers/constants";
import { ProjectFeeTabFormKey } from "../../../../../../modelsAndSchemas/Models";

import { TextFieldCurrency } from "../../../../../../../../components/textFieldCurrency/TextFieldCurrency";
import { commonSiteCoreKeys } from "../../../../../../../../helpers/constants/siteCoreKeys";
import { setApiDataToDropDownOption } from "../../../../../../../../helpers/utils";
import { useMediaQueries } from "../../../../../../../../hooks/useMediaQueries";
import { BillingTypeFieldInvoiceWrapper } from "./BillingTypeFieldInvoice.style";
import { useBillingTypeFieldInvoice } from "./useBillingTypeFieldInvoiceHook";

export interface BillingTypeFieldInvoiceProps {
  pageId: string;
  currencyFormat: CurrencyFormat;
  currencyId: string;
  defaultUserCurrency: string;
  currenciesList: IdNameList[];
  forceUpdate: () => void;
  setShowWarningOnBillingChange: (value: boolean) => void;
  projectStatus: string;
  projectFeeDetailsData?: GetProgramFeeResponse;
}

export const BillingTypeFieldInvoice = forwardRef(
  (props: BillingTypeFieldInvoiceProps, ref) => {
    const {
      pageId,
      gridSize,
      control,
      getLabel,
      fetchDropdownValues,
      fetchValidationMessage,
      commonLabels,
      commonARIALabel,
      billingTypeFormKey,
      billingTypeWatch,
      feeFormKey,
      feeWithFormatFormKey,
      billingTriggerIdFormKey,
      minimumTransferFeeFormKey,
      minimumTransferFeeWithFormatFormKey,
      earlyPayDaysFormKey,
      earlyPayPercentageFormKey,
      proBonoCategoryFormKey,
      isProjectStatusActiveAlready,
      onBillingTypeChange,
      projectFeeDetails,
      projectFee,
      billingTriggerList,
      billingTriggerDropDown,
      billingTypeList,
      billingTypeDropDown,
      proBonoCategoryDropDown,
      proBonoCategoryList,
      earlyPayPercentageToolTipMessage,
      forceUpdate,
      currencyDropDown,
      currenciesList,
      defaultUserCurrency,
      setValue,
      currencyId,
      currencyFormat,
      minimumTransferFee,
      earlyPayDays,
      earlyPayPercentage,
      shouldIgnoreZeroValidationForFee,
      shouldDisableProBonoField,
      shouldDisableProgramFee,
      shouldDisableBillingType,
      shouldDisableOtherFields,
      handleConfirmationStatusTrue,
      handleConfirmationStatusFalse,
    } = useBillingTypeFieldInvoice({
      ...props,
    });

    useImperativeHandle(ref, () => ({
      handleConfirmationStatusTrue,
      handleConfirmationStatusFalse,
    }));

    const { isTablet, isMobile } = useMediaQueries();

    const billingTypeIdFieldInvoice = (
      <MuiGrid size={gridSize}>
        <FormController control={control} name={billingTypeFormKey}>
          <MuiSelect
            id={`${pageId}-${billingTypeFormKey}`}
            label={getLabel(billingTypeDropDown)}
            options={fetchDropdownValues(projectFeeDetails, billingTypeList)}
            defaultValue={""}
            disabled={shouldDisableBillingType}
            visibleDefaultSelectOption={true}
            isMandatory
            placeholder={commonLabels.selectPlaceHolder}
            onChange={onBillingTypeChange}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
    );

    const proBonoFieldsInvoice = (
      <>
        <MuiBox className="dashed-line" />
        <MuiGrid container spacing={2} className="padding-bottom">
          <MuiGrid size={gridSize}>
            <FormController control={control} name={proBonoCategoryFormKey}>
              <MuiSelect
                id={`${pageId}-${proBonoCategoryDropDown}`}
                label={getLabel(proBonoCategoryDropDown)}
                options={fetchDropdownValues(
                  projectFeeDetails,
                  proBonoCategoryList,
                )}
                visibleDefaultSelectOption={true}
                disabled={shouldDisableProBonoField}
                placeholder={commonLabels.selectPlaceHolder}
                onChange={forceUpdate}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </>
    );

    const commonFieldsInvoice = (
      <>
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={`${ProjectFeeTabFormKey.currencyId}`}
          >
            <MuiSelect
              data-testId={`${pageId}-${ProjectFeeTabFormKey.currencyId}`}
              label={getLabel(currencyDropDown)}
              options={setApiDataToDropDownOption(currenciesList || [])}
              defaultValue={defaultUserCurrency}
              showSearch
              onChange={forceUpdate}
              disabled={shouldDisableOtherFields}
              visibleDefaultSelectOption={false}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <FormController control={control} name={feeWithFormatFormKey}>
            <TextFieldCurrency
              isMandatory
              id={`${pageId}-${ProjectFeeTabFormKey.feeWithFormat}`}
              formId={feeWithFormatFormKey}
              locale={currencyFormat?.locale}
              isCurrencyChanged={currencyId}
              disabled={shouldDisableProgramFee}
              ignoreZeroValidation={shouldIgnoreZeroValidationForFee}
              zeroValidationMessage={fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              )}
              formatErrorMessage={fetchValidationMessage(
                commonSiteCoreKeys.alphabetNotAllowedValidator,
              )}
              name={feeWithFormatFormKey}
              label={getLabel(projectFee)}
              placeholder={commonLabels.inputPlaceHolder}
              getFinalValues={(value, valueNonFormatted) => {
                setValue(feeFormKey, valueNonFormatted, {
                  shouldTouch: true,
                });
                setValue(feeWithFormatFormKey, value, {
                  shouldTouch: true,
                });
              }}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={minimumTransferFeeWithFormatFormKey}
          >
            <TextFieldCurrency
              id={`${pageId}-${minimumTransferFeeWithFormatFormKey}`}
              formId={minimumTransferFeeWithFormatFormKey}
              locale={currencyFormat?.locale}
              isCurrencyChanged={currencyId}
              disabled={shouldDisableOtherFields}
              ignoreZeroValidation={shouldIgnoreZeroValidationForFee}
              zeroValidationMessage={fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              )}
              formatErrorMessage={fetchValidationMessage(
                commonSiteCoreKeys.alphabetNotAllowedValidator,
              )}
              name={minimumTransferFeeWithFormatFormKey}
              label={getLabel(minimumTransferFee)}
              placeholder={commonLabels.inputPlaceHolder}
              getFinalValues={(value, valueNonFormatted) => {
                setValue(minimumTransferFeeFormKey, valueNonFormatted, {
                  shouldTouch: true,
                });
                setValue(minimumTransferFeeWithFormatFormKey, value, {
                  shouldTouch: true,
                });
              }}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <FormController control={control} name={earlyPayDaysFormKey}>
            <MuiTextField
              id={`${pageId}-${earlyPayDaysFormKey}`}
              label={getLabel(earlyPayDays)}
              disabled={shouldDisableOtherFields}
              placeholder={commonLabels.inputPlaceHolder}
              type="number"
              maxLength={MAX_LENGTH_100}
              data-testId={`${pageId}-${earlyPayDaysFormKey}`}
              inputNumberWithoutDecimal
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <FormController control={control} name={earlyPayPercentageFormKey}>
            <MuiTextField
              id={`${pageId}-${earlyPayPercentageFormKey}`}
              label={getLabel(earlyPayPercentage)}
              disabled={shouldDisableOtherFields}
              placeholder={commonLabels.inputPlaceHolder}
              type="number"
              maxLength={MAX_LENGTH_100}
              infoMessage={getLabel(earlyPayPercentageToolTipMessage)}
              shouldDisableInfoIcon={shouldDisableOtherFields}
              data-testId={`${pageId}-${earlyPayPercentageFormKey}`}
              inputNumberWithoutDecimal
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </>
    );

    if (
      billingTypeWatch === BILLING_DETAILS_SITE_CORE_VALUE.billingTypeActivity
    ) {
      return (
        <BillingTypeFieldInvoiceWrapper>
          <MuiGrid container spacing={2}>
            {billingTypeIdFieldInvoice}
            <MuiGrid size={gridSize}>
              <FormController control={control} name={billingTriggerIdFormKey}>
                <MuiSelect
                  data-testId={`${pageId}-${ProjectFeeTabFormKey.billingTriggerId}`}
                  label={getLabel(billingTriggerDropDown)}
                  options={fetchDropdownValues(
                    projectFeeDetails,
                    billingTriggerList,
                  )}
                  disabled={isProjectStatusActiveAlready}
                  defaultValue={""}
                  visibleDefaultSelectOption={true}
                  isMandatory
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            {!isTablet && !isMobile && <MuiGrid size={gridSize} />}
            {commonFieldsInvoice}
          </MuiGrid>
          {proBonoFieldsInvoice}
        </BillingTypeFieldInvoiceWrapper>
      );
    } else {
      return (
        <BillingTypeFieldInvoiceWrapper>
          <MuiGrid container spacing={2}>
            {billingTypeIdFieldInvoice}
            {commonFieldsInvoice}
          </MuiGrid>
          {proBonoFieldsInvoice}
        </BillingTypeFieldInvoiceWrapper>
      );
    }
  },
);
